import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";

interface MovementModalProps {
  open: boolean;
  onClose: () => void;
  processing: boolean;
  onSubmit: (type: "ingreso" | "egreso", amount: number, concept: string) => void;
}

export default function MovementModal({ open, onClose, processing, onSubmit }: MovementModalProps) {
  const [type, setType] = useState<"ingreso" | "egreso">("ingreso");
  const [amount, setAmount] = useState("");
  const [concept, setConcept] = useState("");

  useEffect(() => {
    if (!open) return;
    setType("ingreso");
    setAmount("");
    setConcept("");
  }, [open]);

  const parsed = Number(amount) || 0;
  const canSubmit = parsed > 0 && concept.trim().length > 0 && !processing;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Registrar movimiento de caja"
      subtitle="Los movimientos quedan auditados con tu usuario y la fecha."
      size="sm"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onSubmit(type, parsed, concept.trim())}
            disabled={!canSubmit}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {processing ? (
              <i className="ri-loader-4-line animate-spin text-base"></i>
            ) : (
              <i className="ri-check-line text-base"></i>
            )}
            Guardar
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
          <button
            type="button"
            onClick={() => setType("ingreso")}
            className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              type === "ingreso" ? "bg-accent-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            <i className="ri-arrow-down-line text-base"></i>
            Ingreso
          </button>
          <button
            type="button"
            onClick={() => setType("egreso")}
            className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              type === "egreso" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            <i className="ri-arrow-up-line text-base"></i>
            Egreso
          </button>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-foreground-700">Monto (RD$)</label>
          <input
            value={amount}
            onChange={(event) => setAmount(event.target.value.replace(/[^0-9.]/g, ""))}
            inputMode="decimal"
            placeholder="0.00"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-foreground-700">Concepto</label>
          <input
            value={concept}
            onChange={(event) => setConcept(event.target.value)}
            maxLength={120}
            placeholder="Ej: Pago de mensajería, fondo adicional, retiro..."
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400"
          />
        </div>
      </div>
    </Modal>
  );
}